import { faFileLines, faSignature } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, PaperPanel, Stamp } from "@scribenote/brian-design-system";
import { TaskAssigneeBadge } from "@tasks/TaskAssigneeBadge";
import type { Task, TaskViewRef } from "@tasks/tasksClient";
import { useTaskSigning } from "@tasks/useTaskSigning";
import { useWorkspaceNavigation } from "@workspace/useWorkspaceNavigation";

/**
 * A `sign` task (BE-2034), rendered the same on every surface (dashboard tasks slot,
 * notifications feed). Unlike other tasks it acts on a view via
 * `POST /views/{id}/sign`, not `PATCH /tasks`, so it carries its own Sign button and
 * names the view it references, plus an "Open view" link so every surface can jump there.
 *
 * The sign action is offered only to DVMs and practice managers; everyone else sees it as
 * read-only context. The gate and the mutation live in `useTaskSigning`, shared with the
 * index-card presentation on /tasks.
 *
 * Nothing here is optimistic: the Sign button goes busy and the card only says
 * "Signed" once the server has said so. The stamp is a hint at that point, not
 * the record: the signature belongs to the view, and the view says who signed it
 * and when.
 */
export const SignTaskCard = ({
  task,
  view,
  onDecline,
  showOpenView = true,
}: {
  task: Task;
  /** The view being signed: from `task.views[0]`, or supplied by the bell. */
  view: TaskViewRef | undefined;
  onDecline: () => void;
  /** Hidden on the per-view bell, where the view is already open. */
  showOpenView?: boolean;
}) => {
  const { setViewedView } = useWorkspaceNavigation();
  const { isSigner, alreadySigned, canAct, isPending, sign } =
    useTaskSigning(view);

  return (
    <PaperPanel className="p-3" data-testid={`sign-task-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 flex-1 wrap-anywhere text-sm font-medium text-ink">
          {task.title}
        </p>
        {/* Never animated here: a card that arrives already signed was signed
            before this surface was drawn, and nothing was applied on it. The
            /tasks list is the one that stamps a signature as it lands. */}
        {alreadySigned && (
          <Stamp
            tone="accent"
            className="shrink-0"
            data-testid={`sign-task-stamp-${task.id}`}
          >
            Signed
          </Stamp>
        )}
      </div>
      {view && (
        <p
          className="mt-1 truncate text-[0.8125rem] text-annotation"
          data-testid={`sign-task-view-${task.id}`}
        >
          {view.name}
        </p>
      )}
      <TaskAssigneeBadge task={task} className="mt-2" />
      {!view ? (
        <p className="mt-2 text-[0.8125rem] text-annotation">
          Open the view to sign it.
        </p>
      ) : (
        <>
          {(showOpenView || canAct) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {showOpenView && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`sign-task-open-view-${task.id}`}
                  onClick={() => setViewedView(view.id)}
                >
                  <FontAwesomeIcon icon={faFileLines} aria-hidden="true" />
                  Open view
                </Button>
              )}
              {canAct && (
                <>
                  {/* The one act on the card, so it takes the filled ink. The
                      lettering is the stamp's own (mono, uppercase, tracked), so
                      the button reads as the mark it applies, and the two
                      presentations of a sign task rhyme. */}
                  <Button
                    variant="accent"
                    size="sm"
                    busy={isPending}
                    data-testid={`view-sign-${task.id}`}
                    onClick={sign}
                    className="font-mono uppercase tracking-[0.13em]"
                  >
                    <FontAwesomeIcon icon={faSignature} aria-hidden="true" />
                    Sign
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`view-task-dismiss-${task.id}`}
                    onClick={onDecline}
                  >
                    Decline
                  </Button>
                </>
              )}
            </div>
          )}
          {!alreadySigned && !isSigner && (
            <p className="mt-2 text-[0.8125rem] text-annotation">
              Awaiting sign-off by a veterinarian.
            </p>
          )}
        </>
      )}
    </PaperPanel>
  );
};
